/**
 * POS Transaction React Query Hooks
 * Handles sales transaction operations (create, add items, void)
 */

import type {
  AddItemDto,
  CreateTransactionDto,
  CustomerSearchParams,
  PaginatedResponse,
  POSCustomer,
  POSProduct,
  ProductSearchParams,
  SaleTransaction,
  UpdateItemDto,
  VoidTransactionDto,
} from '@/types/pos.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders } from './use-pos-session';

const API_BASE = '/api/v1/pos';
const PRODUCTS_API = '/api/v1/products-direct';
const CUSTOMERS_API = '/api/v1/partners';

// Helper: parse NestJS error response
function parseErrorMessage(error: Record<string, unknown>, fallback: string): string {
  return (error.message as string) || (error.error as { message?: string })?.message || fallback;
}

// Helper: unwrap { data: T } or raw T response
function unwrap<T>(result: unknown): T {
  const obj = result as { data?: T };
  return obj.data ?? (result as T);
}

// ============================================
// API Functions - Transactions
// ============================================

async function createTransaction(dto: CreateTransactionDto): Promise<SaleTransaction> {
  const response = await fetch(`${API_BASE}/transactions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to create transaction'));
  }

  // ⚠️ wire-fidelity (measured dev4, 2026-07-27): a POST /pos/transactions EGYEDULI
  // kivetelkent `{ transaction: {...}, transactionNumber }` burkot ad — a tobbi POS-
  // endpoint nyers objektumot (`GET :id`, `complete`) vagy sajat shape-et (`items`,
  // `payments/cash`, `finalize`). Az `unwrap` csak a `{data:T}` burkot ismeri, ezert
  // itt a burkot visszaadta => `transaction.id === undefined` => a teljes checkout-lanc
  // a `/transactions/undefined/items` hivason elhalt (a UI-bol tetel NELKULI, total=0
  // tranzakciok szulettek — pl. ELADAS-2026-0188). A `transaction` kulcsot explicit bontjuk.
  return unwrapTransaction(await response.json());
}

/**
 * A transaction-valasz kibontasa: `{transaction:T}` (create) VAGY `{data:T}` VAGY nyers T.
 * Wire-fikcio elleni ov — a shape-et a BE oldalrol mertuk, nem feltetelezzuk.
 */
function unwrapTransaction(result: unknown): SaleTransaction {
  const obj = result as { transaction?: SaleTransaction; data?: SaleTransaction };
  return obj.transaction ?? obj.data ?? (result as SaleTransaction);
}

async function fetchTransaction(transactionId: string): Promise<SaleTransaction> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to fetch transaction'));
  }

  return unwrap<SaleTransaction>(await response.json());
}

async function addTransactionItem(
  transactionId: string,
  dto: AddItemDto
): Promise<SaleTransaction> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/items`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to add item'));
  }

  return unwrap<SaleTransaction>(await response.json());
}

async function updateTransactionItem(
  transactionId: string,
  itemId: string,
  dto: UpdateItemDto
): Promise<SaleTransaction> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/items/${itemId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to update item'));
  }

  return unwrap<SaleTransaction>(await response.json());
}

async function removeTransactionItem(
  transactionId: string,
  itemId: string
): Promise<SaleTransaction | null> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/items/${itemId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to remove item'));
  }

  // DELETE may return 204 No Content
  if (response.status === 204) return null;

  return unwrap<SaleTransaction>(await response.json());
}

async function voidTransaction(
  transactionId: string,
  dto: VoidTransactionDto
): Promise<SaleTransaction> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/void`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to void transaction'));
  }

  return unwrap<SaleTransaction>(await response.json());
}

// ============================================
// API Functions - Products
// ============================================

async function searchProducts(params: ProductSearchParams): Promise<POSProduct[]> {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.set('search', params.search);
  if (params.barcode) queryParams.set('barcode', params.barcode);
  if (params.category) queryParams.set('category', params.category);
  if (params.limit) queryParams.set('limit', String(params.limit));

  const response = await fetch(`${PRODUCTS_API}?${queryParams.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to search products'));
  }

  const result = await response.json();
  // Backend may return { data: [...] }, { items: [...] }, or [...]
  const rows =
    (result as PaginatedResponse<POSProduct>).data ??
    (result as { items: POSProduct[] }).items ??
    (result as POSProduct[]);
  return rows.map(toPOSProduct);
}

// Q-T144 follow-up wire-map: a products-direct sorai sellingPriceGross /
// stockQuantity / availableQuantity mezőket adnak (nincs price/stock/unit/barcode)
// → a régi shape-feltevés NaN-árat rajzolt és elhalt az add-to-cart.
type ProductsDirectRow = Partial<POSProduct> & {
  id: string;
  sku: string;
  name: string;
  category: string;
  vatRate: number;
  sellingPriceGross?: number;
  stockQuantity?: number;
  availableQuantity?: number;
};

function toPOSProduct(row: ProductsDirectRow): POSProduct {
  return {
    id: row.id,
    sku: row.sku,
    name: row.name,
    barcode: row.barcode ?? null,
    category: row.category,
    price: row.price ?? row.sellingPriceGross ?? 0,
    vatRate: row.vatRate,
    stock: row.stock ?? row.availableQuantity ?? row.stockQuantity ?? 0,
    unit: row.unit ?? 'db',
  };
}

async function findProductByBarcode(barcode: string): Promise<POSProduct | null> {
  const response = await fetch(`${PRODUCTS_API}?barcode=${encodeURIComponent(barcode)}&limit=1`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to find product'));
  }

  const result = await response.json();
  const items =
    (result as PaginatedResponse<POSProduct>).data ??
    (result as { items: POSProduct[] }).items ??
    (result as POSProduct[]);
  return items[0] ? toPOSProduct(items[0]) : null;
}

// ============================================
// API Functions - Customers
// ============================================

async function searchCustomers(params: CustomerSearchParams): Promise<POSCustomer[]> {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.set('search', params.search);
  if (params.taxNumber) queryParams.set('taxNumber', params.taxNumber);
  if (params.limit) queryParams.set('limit', String(params.limit));

  const response = await fetch(`${CUSTOMERS_API}?${queryParams.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to search customers'));
  }

  const result = await response.json();
  return (
    (result as PaginatedResponse<POSCustomer>).data ??
    (result as { items: POSCustomer[] }).items ??
    (result as POSCustomer[])
  );
}

// ============================================
// Query Keys
// ============================================

export const posTransactionKeys = {
  all: ['pos-transactions'] as const,
  detail: (id: string) => [...posTransactionKeys.all, id] as const,
  products: (params: ProductSearchParams) => ['pos-products', params] as const,
  productByBarcode: (barcode: string) => ['pos-products', 'barcode', barcode] as const,
  customers: (params: CustomerSearchParams) => ['pos-customers', params] as const,
};

// ============================================
// React Query Hooks - Transactions
// ============================================

/**
 * Hook to create a new transaction
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTransaction,
    onSuccess: transaction => {
      queryClient.setQueryData(posTransactionKeys.detail(transaction.id), transaction);
    },
  });
}

/**
 * Hook to fetch a specific transaction
 */
export function useTransaction(transactionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: posTransactionKeys.detail(transactionId),
    queryFn: () => fetchTransaction(transactionId),
    enabled: options?.enabled ?? !!transactionId,
  });
}

/**
 * Hook to add an item to a transaction
 */
export function useAddTransactionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, dto }: { transactionId: string; dto: AddItemDto }) =>
      addTransactionItem(transactionId, dto),
    onSuccess: transaction => {
      queryClient.setQueryData(posTransactionKeys.detail(transaction.id), transaction);
    },
  });
}

/**
 * Hook to update an item in a transaction
 */
export function useUpdateTransactionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      itemId,
      dto,
    }: {
      transactionId: string;
      itemId: string;
      dto: UpdateItemDto;
    }) => updateTransactionItem(transactionId, itemId, dto),
    onSuccess: transaction => {
      queryClient.setQueryData(posTransactionKeys.detail(transaction.id), transaction);
    },
  });
}

/**
 * Hook to remove an item from a transaction
 */
export function useRemoveTransactionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, itemId }: { transactionId: string; itemId: string }) =>
      removeTransactionItem(transactionId, itemId),
    onSuccess: (transaction, variables) => {
      if (transaction) {
        queryClient.setQueryData(posTransactionKeys.detail(transaction.id), transaction);
      }
      queryClient.invalidateQueries({
        queryKey: posTransactionKeys.detail(variables.transactionId),
      });
    },
  });
}

/**
 * Hook to void a transaction
 */
export function useVoidTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, dto }: { transactionId: string; dto: VoidTransactionDto }) =>
      voidTransaction(transactionId, dto),
    onSuccess: transaction => {
      queryClient.setQueryData(posTransactionKeys.detail(transaction.id), transaction);
      queryClient.invalidateQueries({ queryKey: posTransactionKeys.all });
    },
  });
}

// ============================================
// React Query Hooks - Products
// ============================================

/**
 * Hook to search for products
 */
export function useProductSearch(params: ProductSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: posTransactionKeys.products(params),
    queryFn: () => searchProducts(params),
    enabled: options?.enabled ?? !!(params.search || params.barcode || params.category),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to find a product by barcode
 */
export function useFindProductByBarcode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: findProductByBarcode,
    onSuccess: (product, barcode) => {
      if (product) {
        queryClient.setQueryData(posTransactionKeys.productByBarcode(barcode), product);
      }
    },
  });
}

// ============================================
// React Query Hooks - Customers
// ============================================

/**
 * Hook to search for customers
 */
export function useCustomerSearch(params: CustomerSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: posTransactionKeys.customers(params),
    queryFn: () => searchCustomers(params),
    enabled: options?.enabled ?? !!(params.search || params.taxNumber),
    staleTime: 60 * 1000, // 1 minute
  });
}
